import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Loader2 } from 'lucide-react';
import api from '../../services/api';
// Removed mock data import to ensure only real news is shown

const NewsSection = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to real-time news updates
    const unsubscribe = api.news.subscribeToNews((newsData) => {
      // DEBUG: Log the news data to help troubleshoot ghost mock data
      console.group('📰 Home Page News Loading');
      console.log('Total items fetched:', newsData?.length || 0);
      
      // Explicitly filter out known mock data IDs (1, 2, 3, 4) just in case they exist in cache/Firestore
      // This is a strict measure to ensure only newly added admin posts are shown
      const filteredNews = (newsData || []).filter(item => {
        const idStr = String(item.id);
        const isMockId = ['1', '2', '3', '4'].includes(idStr);
        if (isMockId) console.warn('🚫 Blocked mock data item:', item.title);
        return !isMockId;
      });

      console.log('Items after strict filtering:', filteredNews.length);
      console.groupEnd();

      setNews(filteredNews);
      setLoading(false);
    }, { published_only: true, limit: 10 }); // Increased limit slightly to account for filtered items

    // Cleanup subscription on unmount
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <section className="py-8 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Latest News and Events
            </h2>
            <div className="w-16 h-1 bg-[#2A9D8F]" />
          </div>
          <Link
            to="/news"
            className="hidden md:flex items-center text-[#2A9D8F] font-medium hover:underline"
          >
            View All News
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>

        {/* Loading State */}
        {loading && news.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#2A9D8F]" />
          </div>
        ) : (
          /* News Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {news.map((item) => (
              <article
                key={item.id || item.slug}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow group"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden bg-gray-100">
                  {item.image_url || item.image ? (
                    <img
                      src={item.image_url || item.image}
                      alt={item.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=300&fit=crop';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#2A9D8F]/20 to-[#004488]/20">
                      <span className="text-4xl">📰</span>
                    </div>
                  )}
                  {/* Date badge */}
                  <div className="absolute top-3 left-3 bg-[#2A9D8F] text-white text-xs px-2 py-1 rounded flex items-center">
                    <Calendar size={12} className="mr-1" />
                    {formatDate(item.date || item.created_at)}
                  </div>
                  {/* Category badge */}
                  {item.category && (
                    <div className="absolute top-3 right-3 bg-white/90 text-gray-700 text-xs px-2 py-1 rounded">
                      {item.category}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#2A9D8F] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                    {item.summary || item.excerpt || item.content?.substring(0, 100)}
                  </p>
                  <Link
                    to={`/news/${item.slug}`}
                    className="text-[#2A9D8F] text-sm font-medium flex items-center hover:underline"
                  >
                    Read More
                    <ArrowRight size={14} className="ml-1" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && news.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No news articles yet. Check back soon!</p>
          </div>
        )}

        {/* Mobile View All Button */}
        <div className="mt-8 text-center md:hidden">
          <Link
            to="/news"
            className="inline-flex items-center bg-[#2A9D8F] text-white px-6 py-3 rounded font-medium hover:bg-[#0055a0] transition-colors"
          >
            View All News
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
